/**
 * 🌐 API client بسيط للنداءات HTTP
 * يستخدم proxy في Vite (/api → http://localhost:3000)
 */

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
}

class ApiError extends Error {
  /** بيانات إضافية ترجعها الـ API (مثل quotaExceeded, upgradeUrl, ...) */
  data?: Record<string, unknown>;
  constructor(message: string, public status: number, public fieldErrors?: Record<string, string[]>, data?: Record<string, unknown>) {
    super(message);
    this.data = data;
  }
}

async function request<T = unknown>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  let url = path.startsWith('http') ? path : path;
  if (query) {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }

  const res = await fetch(url, {
    ...rest,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.success === false) {
    // 🔧 استخراج رسالة قابلة للقراءة (zod ترجع object فيه issues)
    let msg: string = json?.error ?? `HTTP ${res.status}`;
    if (typeof msg === 'object' && msg !== null) {
      const issues = (msg as { issues?: Array<{ path?: (string|number)[]; message?: string; code?: string }> }).issues;
      if (Array.isArray(issues) && issues.length > 0) {
        msg = issues
          .map((i) => {
            const field = arabicFieldName((i.path ?? []).join('.'));
            const reason = arabicReason(i.message ?? '', i.code);
            return field ? `${field}: ${reason}` : reason;
          })
          .filter(Boolean)
          .join(' • ');
      } else {
        try { msg = JSON.stringify(msg); } catch { msg = 'حدث خطأ'; }
      }
    }
    throw new ApiError(String(msg), res.status, json?.fieldErrors, json);
  }
  return json as T;
}

// 🇸🇦 ترجمة أسماء الحقول الشائعة للعربية
const FIELD_NAMES: Record<string, string> = {
  name: 'الاسم', nameEn: 'الاسم بالإنجليزي',
  email: 'البريد الإلكتروني', phone: 'رقم الجوال',
  password: 'كلمة المرور', confirmPassword: 'تأكيد كلمة المرور',
  title: 'العنوان', description: 'الوصف',
  price: 'السعر', area: 'المساحة', rooms: 'عدد الغرف',
  city: 'المدينة', district: 'الحي', address: 'العنوان التفصيلي',
  latitude: 'خط العرض', longitude: 'خط الطول',
  type: 'النوع', purpose: 'الغرض', status: 'الحالة',
  logo: 'الشعار', coverImage: 'صورة الغلاف', image: 'الصورة',
  companyId: 'شركة الإدارة', totalUnits: 'إجمالي الوحدات', totalBuildings: 'عدد المباني',
  bankIban: 'رقم الآيبان', bankName: 'اسم البنك',
  falLicenseNumber: 'رقم رخصة فال', fullName: 'الاسم الكامل',
  reason: 'السبب', message: 'الرسالة',
};

function arabicFieldName(path: string): string {
  if (!path) return '';
  // path قد يكون "field" أو "items.0.title"
  const last = path.split('.').pop() ?? path;
  return FIELD_NAMES[last] ?? last;
}

function arabicReason(message: string, code?: string): string {
  // ترجمة رسائل Zod الشائعة
  if (/required/i.test(message) || code === 'invalid_type') return 'مطلوب';
  if (/email/i.test(message)) return 'بريد إلكتروني غير صحيح';
  if (/url/i.test(message)) return 'رابط غير صحيح';
  if (/too_small|min/i.test(message) || code === 'too_small') return 'القيمة صغيرة جداً';
  if (/too_big|max/i.test(message) || code === 'too_big') return 'القيمة كبيرة جداً';
  if (/uuid|cuid/i.test(message)) return 'معرّف غير صحيح';
  if (/enum|invalid.*value/i.test(message)) return 'قيمة غير مسموحة';
  if (/string/i.test(message) && code === 'invalid_type') return 'يجب أن يكون نصاً';
  if (/number/i.test(message) && code === 'invalid_type') return 'يجب أن يكون رقماً';
  return message || 'قيمة غير صالحة';
}

export const api = {
  get:    <T = unknown>(path: string, options?: FetchOptions) => request<T>(path, { ...options, method: 'GET' }),
  post:   <T = unknown>(path: string, body?: unknown, options?: FetchOptions) => request<T>(path, { ...options, method: 'POST', body }),
  put:    <T = unknown>(path: string, body?: unknown, options?: FetchOptions) => request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T = unknown>(path: string, options?: FetchOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};

export { ApiError };

// 🌐 أنواع موحّدة لاستجابات API
export interface ApiSuccess<T = unknown> {
  success: true;
  data: T;
  message?: string;
}
export interface Paginated<T> {
  success: true;
  data: T[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}
